import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { formatCurrency, groupPlansByCycle } from './service';
import { useSubscriptionPlans, useActiveSubscription, useCreateCheckoutSession } from './useSubscriptionQueries';

const formatCycleLabel = (cycle) => {
  if (!cycle) return 'Monthly';
  const value = cycle.toLowerCase();
  switch (value) {
    case 'daily':
      return 'Billed Daily';
    case 'weekly':
      return 'Billed Weekly';
    case 'monthly':
      return 'Billed Monthly';
    case 'yearly':
      return 'Billed Annually';
    default:
      return `Billed ${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }
};

const PlanCard = ({ plan, activeBillingCycle, onChoosePlan, isLoading, activeSubscription }) => {
  const product = plan.product || {};
  const features = Array.isArray(plan.features) && plan.features.length
    ? plan.features
    : Array.isArray(product.features) && product.features.length
      ? product.features
      : [];
  const priceLabel =
    plan.pricingModel === 'per-user'
      ? 'per user (base price)'
      : 'flat rate';
  
  // Determine which price to show based on active billing cycle
  const isMonthly = activeBillingCycle === 'monthly';
  const isYearly = activeBillingCycle === 'yearly';
  
  // Get the appropriate price for the selected billing cycle
  const displayPrice = isMonthly && typeof plan.monthlyPrice === 'number'
    ? plan.monthlyPrice
    : isYearly && typeof plan.yearlyPrice === 'number'
      ? plan.yearlyPrice
      : plan.price || 0;
  
  const trialLabel =
    typeof plan.trialPeriod === 'number' && plan.trialPeriod > 0
      ? `${plan.trialPeriod} day trial`
      : null;

  // Check if this is the active plan
  const isActivePlan = activeSubscription && activeSubscription.productId === plan.id;
  
  // Check if this plan is lower tier (lower price) than active plan
  // Only compare if on the same billing cycle
  let isLowerTier = false;
  if (activeSubscription && !activeSubscription.isExpired && activeSubscription.billingCycle === activeBillingCycle) {
    // Get the price for the same billing cycle as active subscription
    const activeCyclePrice = activeBillingCycle === 'monthly' && typeof plan.monthlyPrice === 'number'
      ? plan.monthlyPrice
      : activeBillingCycle === 'yearly' && typeof plan.yearlyPrice === 'number'
        ? plan.yearlyPrice
        : plan.price || 0;
    
    // Compare with active subscription amount (normalize currency if needed)
    if (plan.currency === activeSubscription.currency) {
      isLowerTier = activeCyclePrice < activeSubscription.amount;
    }
  }
  
  // Determine button state
  const isDisabled = isLoading || isActivePlan || isLowerTier;
  const buttonText = isActivePlan ? 'Active' : isLoading ? 'Processing...' : 'Choose Plan';
  const buttonClassName = isActivePlan ? 'get-started-btn active-plan-btn' : 'get-started-btn';

  return (
    <div className="plan-box" key={plan.id}>
      <div className='top-plan-box'>
        <h4>{plan.name || product.name || 'Subscription Plan'}</h4>
        <div className="plan-price-block">
          <span className="plan-price">{formatCurrency(displayPrice, plan.currency)}</span>
          <p>{priceLabel} · {formatCycleLabel(activeBillingCycle || plan.billingCycle)}</p>
          {trialLabel && (
            <div className="plan-price-breakdown">
              <div className="plan-price-line trial-label">
                <span>{trialLabel}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='top-plan-feature'>
        {product.description && <p className="plan-description">{product.description}</p>}
        {features.length > 0 && (
          <ul>
            {features.map((feature, index) => (
              <li key={`${plan.id}-feature-${index}`}>{feature}</li>
            ))}
          </ul>
        )}
        {plan.duration && (
          <div className="plan-meta">
            <span>Duration: {formatCycleLabel(plan.duration)}</span>
          </div>
        )}
        <button 
          className={buttonClassName}
          type="button" 
          onClick={() => onChoosePlan(plan)}
          disabled={isDisabled}
          style={isActivePlan ? {
            backgroundColor: '#10b981',
            color: 'white',
            cursor: 'not-allowed',
            opacity: 1
          } : isLowerTier ? {
            opacity: 0.6,
            cursor: 'not-allowed'
          } : {}}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const SubscriptionPlans = () => {
  const [tab, setTab] = useState('monthly');
  const [processingPlan, setProcessingPlan] = useState(null);
  const token = useSelector((state) => state.auth.token);

  // Use React Query hooks for data fetching
  const { 
    data: plans = [], 
    isLoading: plansLoading, 
    error: plansError 
  } = useSubscriptionPlans(token);

  const { 
    data: activeSubscription = null, 
    isLoading: subscriptionLoading 
  } = useActiveSubscription(token);

  const createCheckoutMutation = useCreateCheckoutSession();

  // Set default tab based on available plans
  useEffect(() => {
    if (plans.length > 0 && plans.some(plan => plan.billingCycle === 'yearly')) {
      setTab('yearly');
    }
  }, [plans]);

  const groupedPlans = useMemo(() => groupPlansByCycle(plans), [plans]);
  const availableTabs = useMemo(() => {
    const cycles = Object.keys(groupedPlans);
    if (cycles.length === 0) {
      return ['monthly'];
    }
    return cycles;
  }, [groupedPlans]);
  const activeTab = availableTabs.includes(tab) ? tab : availableTabs[0];
  
  // Sort visible plans by amount (ascending - lowest first) based on active billing cycle
  const visiblePlans = useMemo(() => {
    const plansForTab = groupedPlans[activeTab] || [];
    return [...plansForTab].sort((a, b) => {
      // Get the price for the active billing cycle
      const getPrice = (plan) => {
        const isMonthly = activeTab === 'monthly';
        const isYearly = activeTab === 'yearly';
        
        if (isMonthly && typeof plan.monthlyPrice === 'number') {
          return plan.monthlyPrice;
        }
        if (isYearly && typeof plan.yearlyPrice === 'number') {
          return plan.yearlyPrice;
        }
        return plan.price || 0;
      };
      
      const priceA = getPrice(a);
      const priceB = getPrice(b);
      
      // Sort ascending (lower price first)
      return priceA - priceB;
    });
  }, [groupedPlans, activeTab]);

  const handleTabClick = (value) => {
    setTab(value);
  };

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  const handleChoosePlan = async (plan) => {
    if (!token) {
      toast.error('Please log in to continue', { position: "bottom-right", autoClose: 3000 });
      return;
    }

    // Check if this is the active plan
    if (activeSubscription && activeSubscription.productId === plan.id) {
      toast.info('This is your current active plan', { position: "bottom-right", autoClose: 3000 });
      return;
    }

    // Check if this is a lower tier plan (only compare if same billing cycle)
    if (activeSubscription && !activeSubscription.isExpired && activeSubscription.billingCycle === activeTab) {
      const isMonthly = activeTab === 'monthly';
      const isYearly = activeTab === 'yearly';
      const displayPrice = isMonthly && typeof plan.monthlyPrice === 'number'
        ? plan.monthlyPrice
        : isYearly && typeof plan.yearlyPrice === 'number'
          ? plan.yearlyPrice
          : plan.price || 0;

      // Only compare if same currency
      if (plan.currency === activeSubscription.currency && displayPrice < activeSubscription.amount) {
        toast.warning('You can only upgrade to a higher tier plan. Please choose a plan with a higher price.', { 
          position: "bottom-right", 
          autoClose: 4000 
        });
        return;
      }
    }

    try {
      setProcessingPlan(plan.id);
      
      const response = await createCheckoutMutation.mutateAsync({
        token,
        productId: plan.id,
        billingCycle: activeTab || plan.billingCycle
      });

      if (response.status === 'success' && response.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(
        error?.response?.data?.message || error.message || 'Failed to initiate payment. Please try again.',
        { position: "bottom-right", autoClose: 3000 }
      );
      setProcessingPlan(null);
    }
  };

  return (
    <div className="subscription-plans">
        <div className="subscription-plans-heading">
            <h3>Talenton Subscriptions</h3>
            <p>Choose the subscription plan that best suits your needs.</p>
        </div>
      <div className='subscription-plans-inner'>
        {(plansLoading || subscriptionLoading) ? (
          <>
            {/* Tabs Skeleton */}
            <div className="tabs" style={{ display: 'inline-flex', padding: '8px', gap: '12px', borderRadius: '100px', border: '1px solid #E4E4E4', background: '#FFF' }}>
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`skeleton-tab-${index}`}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '37px',
                    width: '120px',
                    height: '37px',
                    backgroundColor: '#e5e7eb',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
            {/* Plans Skeleton */}
            <div className="plans">
              {Array.from({ length: 3 }).map((_, index) => (
                <div className="plan-box" key={`skeleton-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
                  <div className="top-plan-box">
                    {/* Plan Name Skeleton */}
                    <div
                      style={{
                        width: "200px",
                        height: "28.8px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        animation: "pulse 1.5s ease-in-out infinite",
                        marginBottom: "16px",
                      }}
                    />
                    <div className="plan-price-block">
                      {/* Price Skeleton */}
                      <div
                        style={{
                          width: "120px",
                          height: "38.4px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                          marginBottom: "8px",
                        }}
                      />
                      {/* Price Label Skeleton */}
                      <div
                        style={{
                          width: "180px",
                          height: "16.8px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>
                  <div className="top-plan-feature">
                    {/* Description Skeleton */}
                    <div
                      style={{
                        width: "100%",
                        height: "21px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        animation: "pulse 1.5s ease-in-out infinite",
                        marginBottom: "14px",
                      }}
                    />
                    {/* Features List Skeleton */}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {Array.from({ length: 4 }).map((_, featureIndex) => (
                        <li key={`skeleton-feature-${featureIndex}`} style={{ marginBottom: "12px", paddingLeft: "32px", position: "relative" }}>
                          <div
                            style={{
                              width: "220px",
                              height: "21px",
                              backgroundColor: "#e5e7eb",
                              borderRadius: "4px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                    {/* Button Skeleton */}
                    <div
                      style={{
                        width: "100%",
                        height: "40px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "6px",
                        animation: "pulse 1.5s ease-in-out infinite",
                        marginTop: "24px",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="tabs">
              {availableTabs.map((cycle) => (
                <button
                  key={cycle}
                  className={`tab-button ${activeTab === cycle ? 'active' : ''}`}
                  onClick={() => handleTabClick(cycle)}
                >
                  {formatCycleLabel(cycle)}
                </button>
              ))}
            </div>
            <div className="plans">
              {plansError && !plansLoading && <div className="plan-error">{plansError?.response?.data?.message || plansError?.message || 'Unable to load subscription plans'}</div>}
              {!plansLoading && !plansError && visiblePlans.length === 0 && (
                <div className="plan-empty">No plans available for this billing cycle.</div>
              )}
              {!plansLoading && !plansError && visiblePlans.map((plan) => (
                <PlanCard 
                  key={plan.id} 
                  plan={plan} 
                  activeBillingCycle={activeTab}
                  onChoosePlan={handleChoosePlan}
                  isLoading={processingPlan === plan.id}
                  activeSubscription={activeSubscription}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlans;